import * as React from 'react';
import {useSelector} from 'react-redux';
import {Link as RoutedLink} from 'react-router-dom';

import {formValueSelector} from 'redux-form';

import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import type {NutshellState} from '../../../store';

import {LOGIN_REDUX_FORM} from '../constants';
import {isIOSSignup, isMobileSignup, showLoginForm} from '../utils';

import styles from './main-header.module.css';

const loginFormEmailSelector = formValueSelector(LOGIN_REDUX_FORM);

type Props = {
    showSignupLink?: boolean;
};

export function MainHeader(props: Props) {
    const containerRef = React.useRef(null);
    const loginFormEmail = useSelector((state: NutshellState) =>
        loginFormEmailSelector(state, 'username')
    );
    const emailValue = loginFormEmail;
    const [width] = useResizeObserver(containerRef.current);

    // If we're coming from the microsite (nutshell.com/m), ensure we link back accordingly on
    // logo click
    const searchParams = new URLSearchParams(window.location.search);
    const isFromMicrosite = searchParams.get('from') === 'm';
    const logoHref = isFromMicrosite
        ? 'https://www.nutshell.com/m/'
        : 'https://www.nutshell.com/home';

    const buildNavSearch = () => {
        const params = new URLSearchParams();
        if (emailValue) params.set('email', emailValue);
        if (isFromMicrosite) params.set('from', 'm');

        const str = params.toString();

        return str ? `?${str}` : undefined;
    };

    return (
        <div ref={containerRef} className={styles['main-header-container']}>
            <Link href={logoHref}>
                <img
                    src='/include/images/nutshell-logo-full-color.svg'
                    alt='nutshell logo'
                    className={styles['nutshell-logo']}
                />
            </Link>
            {!isIOSSignup() && (
                <Body size='large' color='medium'>
                    <span className={styles['login-link-pretext']}>
                        {props.showSignupLink
                            ? 'Don’t have an account?'
                            : 'Already have an account?'}
                        &nbsp;&nbsp;
                    </span>
                    <Link
                        // @ts-expect-error
                        as={RoutedLink}
                        variant='primary'
                        size='big'
                        isBold={true}
                        onClick={handleLoginClick}
                        to={{
                            pathname: props.showSignupLink ? '/signup' : '/auth',
                            search: buildNavSearch(),
                        }}
                        className={styles['login-link']}
                    >
                        {getSignupText(Boolean(props.showSignupLink), width)}
                    </Link>
                </Body>
            )}
        </div>
    );
}

export function handleLoginClick(e: Event) {
    if (!isMobileSignup()) {
        return;
    }

    e.stopPropagation();

    showLoginForm();
}

function getSignupText(showSignupLink: boolean, width?: number | null) {
    if (!showSignupLink) {
        return 'Sign in';
    }

    // text will wrap below this width, so switch to shorter text in that case
    if (width && width > 325) {
        return 'Sign up for free trial';
    }

    return 'Sign up';
}
